import { useEffect, useState } from "react";
import { ArrowSquareOut, CalendarBlank, FilePdf, Image, Users } from "@phosphor-icons/react";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportsPage() {
  const { t } = useLanguage();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/kybia-reports`)
      .then((r) => r.json())
      .then((data) => setReports(Array.isArray(data) ? data : []))
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="reports-page">
      <section
        className="py-20 md:py-28 border-b"
        style={{ backgroundColor: "#E8EDE9", borderColor: "#D3DCD6" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p
            className="text-xs uppercase tracking-[0.2em] font-medium mb-4"
            style={{ color: "#B85C38" }}
          >
            {t("reports_tagline")}
          </p>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium"
            style={{ color: "#1C211F" }}
          >
            {t("reports_heading")}
          </h1>
        </div>
      </section>

      <section
        className="py-20 md:py-28"
        style={{ backgroundColor: "#F7F5F0" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-12">
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4A5450" }}>
              {t("reports_desc")}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-7 h-7 border-2 rounded-full animate-spin" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
                <FilePdf size={28} weight="duotone" style={{ color: "#1A5C37" }} />
              </div>
              <p className="text-sm font-medium" style={{ color: "#1C211F" }}>{t("no_reports")}</p>
              <p className="text-xs mt-1" style={{ color: "#4A5450" }}>{t("no_reports_sub")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reports.map((report, index) => (
                <a
                  key={report.id}
                  href={`${BACKEND_URL}${report.file_url}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`kybia-report-card-${index}`}
                  className="text-left p-6 rounded-lg border transition-all duration-200 group flex flex-col no-underline"
                  style={{
                    backgroundColor: "#FFFFFF",
                    borderColor: "rgba(26, 92, 55, 0.1)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(26, 92, 55, 0.35)";
                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(26,92,55,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(26, 92, 55, 0.1)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: "rgba(26, 92, 55, 0.08)" }}
                  >
                    {report.file_type === "pdf"
                      ? <FilePdf size={20} weight="duotone" style={{ color: "#1A5C37" }} />
                      : <Image size={20} weight="duotone" style={{ color: "#1A5C37" }} />}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="flex items-center gap-1 text-xs" style={{ color: "#4A5450" }}>
                      <CalendarBlank size={12} weight="duotone" />
                      {report.year}
                    </span>
                  </div>

                  <h2
                    className="font-heading text-lg font-medium mb-2 leading-snug group-hover:underline"
                    style={{ color: "#1C211F" }}
                  >
                    {report.title}
                  </h2>

                  <p className="flex items-start gap-1.5 text-xs mb-3" style={{ color: "#4A5450" }}>
                    <Users size={12} weight="duotone" className="mt-0.5 flex-shrink-0" />
                    {report.publisher}
                  </p>

                  <p className="text-sm leading-relaxed flex-1" style={{ color: "#4A5450" }}>
                    {report.description}
                  </p>

                  <div className="flex items-center gap-1.5 mt-4 text-xs font-medium" style={{ color: "#1A5C37" }}>
                    <ArrowSquareOut size={14} weight="duotone" />
                    {t("open_pdf")}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
